﻿// ── Login Page Logic ──────────────────────────────────────────────────────────
// Handles Sign In, magic link and forgot/reset password on login.html.
// Account creation lives elsewhere: signup.html (new company) and
// accept-invite.html (joining an existing company).

// Listen for recovery callback (when user clicks the link in the reset email).
// Supabase fires PASSWORD_RECOVERY when the URL hash contains a recovery token.
sbClient.auth.onAuthStateChange((event) => {
  if (event === 'PASSWORD_RECOVERY') {
    _isRecovering = true;
    showTab('reset');
  }
});

let _isRecovering = false;

(async () => {
  // If the URL has a recovery hash, don't redirect - wait for PASSWORD_RECOVERY event
  const hash = window.location.hash || '';
  if (hash.includes('type=recovery')) {
    _isRecovering = true;
    showTab('reset');
    return;
  }

  // If already authenticated (normal session), forward to the app
  const { data: { session } } = await sbClient.auth.getSession();
  if (session && sessionIsDeactivated(session)) {
    await _deactivatedSignOut();
    document.getElementById('siEmail')?.focus();
    return;
  }
  if (session && !_isRecovering) {
    window.location.href = 'index.html';
    return;
  }

  if (new URLSearchParams(location.search).has('deactivated')) await _deactivatedSignOut();

  // Focus email field on load
  document.getElementById('siEmail')?.focus();
})();

// ── Tab switching ─────────────────────────────────────────────────────────────
function showTab(tab) {
  const forms = {
    signin:   document.getElementById('formSignIn'),
    forgot:   document.getElementById('formForgot'),
    reset:    document.getElementById('formReset'),
  };
  // Hide all
  Object.values(forms).forEach(f => f?.classList.add('hidden'));
  // Show the selected one
  forms[tab]?.classList.remove('hidden');

  // Tab buttons (top) only show for the signin flow
  const tabsRow = document.querySelector('.lp-tabs');
  if (tabsRow) tabsRow.style.display = (tab === 'signin') ? '' : 'none';

  document.getElementById('tabSignIn')?.classList.toggle('active', tab === 'signin');

  // Titles
  const titles = {
    signin:   { title: 'Welcome back',         sub: 'Sign in to your crew’s safety system.' },
    forgot:   { title: 'Reset your password',  sub: 'Enter your email and we will send you a reset link.' },
    reset:    { title: 'Set a new password',   sub: 'Choose a new password for your account.' },
  };
  const t = titles[tab] || titles.signin;
  document.getElementById('cardTitle').textContent = t.title;
  document.getElementById('cardSub').textContent   = t.sub;

  clearErrors();

  // Auto-focus the right input
  const firstInputId = {
    signin: 'siEmail', forgot: 'fpEmail', reset: 'rpPassword'
  }[tab];
  document.getElementById(firstInputId)?.focus();
}

// ── Sign In ───────────────────────────────────────────────────────────────────
async function handleSignIn() {
  const email    = document.getElementById('siEmail')?.value.trim()    ?? '';
  const password = document.getElementById('siPassword')?.value         ?? '';
  const btn      = document.getElementById('siBtn');
  const errEl    = document.getElementById('siError');

  if (!email || !password) {
    showError(errEl, 'Please fill in all fields.');
    return;
  }

  setLoading(btn, true, 'Signing in…');
  clearEl(errEl);

  const { data, error } = await sbClient.auth.signInWithPassword({ email, password });

  if (error) {
    showError(errEl, friendlyError(error.message));
    setLoading(btn, false, 'Sign in');
    return;
  }

  if (sessionIsDeactivated(data?.session)) {
    await _deactivatedSignOut();
    setLoading(btn, false, 'Sign in');
    return;
  }

  // Success — hand off to app
  window.location.href = 'index.html';
}

// Removed from the company: sign out on this device only (no network needed)
// and say why, instead of bouncing between the login page and the app.
async function _deactivatedSignOut() {
  try { await sbClient.auth.signOut({ scope: 'local' }); } catch (_) {}
  showError(document.getElementById('siError'),
    'This account has been deactivated. Ask your company admin if you think this is a mistake.');
}

// Password visibility toggle (reference: eye button inside the field).
function togglePw(inputId, btn) {
  const el = document.getElementById(inputId);
  if (!el) return;
  const show = el.type === 'password';
  el.type = show ? 'text' : 'password';
  btn.setAttribute('aria-label', show ? 'Hide password' : 'Show password');
  btn.innerHTML = `<i data-lucide="${show ? 'eye-off' : 'eye'}"></i>`;
  window.lucide && lucide.createIcons();
}

// setLoading() sets textContent, which would delete the button's stroke icon.
function _setMlBtn(loading, label) {
  const btn = document.getElementById('mlBtn');
  if (!btn) return;
  btn.disabled = loading;
  btn.classList.toggle('is-loading', loading);
  btn.innerHTML = loading
    ? `<i data-lucide="loader"></i>${label || 'Sending…'}`
    : '<i data-lucide="mail"></i>Email me a magic link';
  window.lucide && lucide.createIcons();
}

// ── Magic link (passwordless) ─────────────────────────────────────
// Closed decision (Renan, 2026-07-24): the field crew forgets passwords, so a
// one-tap email link signs them in. Password sign-in is untouched — this is an
// additional door, not a replacement. Supabase sends the link; clicking it
// returns here with a session and the app boots normally.
async function handleMagicLink() {
  const email  = document.getElementById('siEmail')?.value.trim() ?? '';
  const btn    = document.getElementById('mlBtn');
  const errEl  = document.getElementById('siError');
  const succEl = document.getElementById('mlSuccess');

  clearEl(errEl); clearEl(succEl);
  if (!email) {
    showError(errEl, "Enter your email first — we'll send the link there.");
    document.getElementById('siEmail')?.focus();
    return;
  }

  _setMlBtn(true, 'Sending…');
  // Land back on the app itself; Supabase appends the session to the URL.
  const emailRedirectTo = `${window.location.origin}/index.html`;
  const { error } = await sbClient.auth.signInWithOtp({
    email,
    options: { emailRedirectTo, shouldCreateUser: false },   // invite-only product
  });

  if (error) {
    showError(errEl, friendlyError(error.message));
    _setMlBtn(false);
    return;
  }

  showEl(succEl,
    "If that email is on your team, the link is on its way — it expires in an hour.");
  _setMlBtn(false);
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function setLoading(btn, loading, text) {
  if (!btn) return;
  btn.disabled = loading;
  btn.textContent = text;
}

function showError(el, msg) {
  if (!el) return;
  el.textContent = msg;
  el.classList.add('show');
}

function showEl(el, msg) {
  if (!el) return;
  el.textContent = msg;
  el.classList.add('show');
}

function clearEl(el) {
  if (!el) return;
  el.textContent = '';
  el.classList.remove('show');
}

function clearErrors() {
  ['siError','fpError','fpSuccess','rpError','rpSuccess']
    .forEach(id => clearEl(document.getElementById(id)));
}

// ── Forgot Password ───────────────────────────────────────────────────────────
async function handleForgotPassword() {
  const email  = document.getElementById('fpEmail')?.value.trim() ?? '';
  const btn    = document.getElementById('fpBtn');
  const errEl  = document.getElementById('fpError');
  const succEl = document.getElementById('fpSuccess');

  clearEl(errEl); clearEl(succEl);
  if (!email) { showError(errEl, 'Please enter your email.'); return; }

  setLoading(btn, true, 'Sending…');

  const redirectTo = `${window.location.origin}${window.location.pathname}`;
  const { error } = await sbClient.auth.resetPasswordForEmail(email, { redirectTo });

  if (error) {
    showError(errEl, friendlyError(error.message));
    setLoading(btn, false, 'Send reset link');
    return;
  }

  showEl(succEl,
    'If an account exists for that email, a reset link is on its way. ' +
    'Check your inbox (and spam folder).');
  setLoading(btn, false, 'Send reset link');
  document.getElementById('fpEmail').value = '';
}

// ── Reset Password (after clicking link in email) ─────────────────────────────
async function handleResetPassword() {
  const p1    = document.getElementById('rpPassword')?.value  ?? '';
  const p2    = document.getElementById('rpPassword2')?.value ?? '';
  const btn   = document.getElementById('rpBtn');
  const errEl = document.getElementById('rpError');
  const succEl= document.getElementById('rpSuccess');

  clearEl(errEl); clearEl(succEl);
  if (p1.length < 8)  { showError(errEl, 'Password must be at least 8 characters.'); return; }
  if (p1 !== p2)      { showError(errEl, 'The two passwords do not match.'); return; }

  setLoading(btn, true, 'Saving…');

  const { error } = await sbClient.auth.updateUser({ password: p1 });

  if (error) {
    showError(errEl, friendlyError(error.message));
    setLoading(btn, false, 'Set new password');
    return;
  }

  showEl(succEl, 'Password updated! Redirecting…');
  // Clear the recovery hash so reloading doesn't keep us in recovery mode
  history.replaceState(null, '', window.location.pathname);
  setTimeout(() => { window.location.href = 'index.html'; }, 1200);
}

function friendlyError(msg) {
  if (msg && typeof msg !== 'string') msg = msg.message || msg.error_description || '';
  if (!msg) return 'Something went wrong. Please try again.';
  const m = msg.toLowerCase();
  // Any auth 5xx (mailer down, Supabase incident) reaches here as the literal
  // "{}": supabase-js builds the message with JSON.stringify(response) before
  // the body is read. Sign-in, magic link and reset all showed it as-is.
  if (m === '{}' || m.startsWith('{') || m.includes('unexpected_failure') || m.includes('error sending'))
    return 'The sign-in server could not answer just now. Please try again in a minute.';
  if (m.includes('invalid login') || m.includes('invalid credentials')) return 'Incorrect email or password.';
  if (m.includes('email not confirmed')) return 'Please confirm your email before signing in.';
  if (m.includes('user already registered')) return 'An account with this email already exists. Try signing in.';
  // Only the two password refusals Supabase actually sends get a rewrite; any
  // other message containing "password" is shown as it is (app review B9).
  if (m.includes('different from the old') || m.includes('same_password'))
    return 'Your new password must be different from the current one.';
  if (m.includes('password') && (m.includes('at least') || m.includes('short')))
    return 'Password must be at least 8 characters.';
  if (m.includes('password') && (m.includes('weak') || m.includes('guess')))
    return 'That password is too easy to guess — pick a longer or less common one.';
  if (m.includes('rate limit')) return 'Too many attempts. Please wait a moment and try again.';
  // Magic link with shouldCreateUser:false — Supabase reports an unknown email
  // as a signup refusal. The crew member needs an invite, not a retry.
  if (m.includes('signups not allowed') || m.includes('signup is disabled'))
    return 'No account for that email. Ask your admin to invite you.';
  if (m.includes('otp') && m.includes('expired')) return 'That link expired. Send yourself a new one.';
  return msg;
}
